import { FC, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  getCollectionShares,
  updateCollectionShares,
  updateCollection,
  type Collection,
  type CollectionType,
} from "../../../../apis/bacchus/collections";
import { getTenantUsers, type TenantUser } from "../../../../apis/emback/users";
import { getLoggedUserProfile } from "../../../../utils/auth";
import { useSnackbar } from "../../../../components/Snackbar";
import { useCollectionsContext } from "../hooks/useCollectionsContext";

type SharingMode = "private" | "specific" | "org";

interface Props {
  collection: Collection;
  onClose: () => void;
}

export const ShareDialog: FC<Props> = ({ collection, onClose }) => {
  const collectionType = collection.type.replace(
    /^rgif_|_collection$/g,
    ""
  ) as CollectionType;

  const [mode, setMode] = useState<SharingMode>(() => {
    if (collection.visibility === "public") return "org";
    return "private";
  });
  const [allUsers, setAllUsers] = useState<TenantUser[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<TenantUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);

  const { showSuccess, showError } = useSnackbar();
  const { refreshCollections } = useCollectionsContext();

  // Load current shares and tenant users
  useEffect(() => {
    const load = async () => {
      setLoadingUsers(true);
      try {
        const profile = getLoggedUserProfile();
        const [sharesResult, users] = await Promise.all([
          getCollectionShares(collectionType, collection.id),
          profile?.oktaUserId
            ? getTenantUsers(profile.oktaUserId)
            : Promise.resolve([]),
        ]);

        setAllUsers(users.filter((u) => u.email !== profile?.email));

        // Pre-select users that already have shares
        if (sharesResult.data && sharesResult.data.length > 0) {
          const sharedEmails = new Set(
            sharesResult.data.map((s) => s.email).filter(Boolean)
          );
          const preSelected = users.filter((u) => sharedEmails.has(u.email));
          setSelectedUsers(preSelected);

          // If there are existing specific shares, switch to "specific" mode
          if (preSelected.length > 0 && collection.visibility !== "public") {
            setMode("specific");
          }
        }
      } catch {
        // Non-fatal — user can still choose mode
      } finally {
        setLoadingUsers(false);
      }
    };

    load();
  }, [collection.id, collection.visibility, collectionType]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const basePayload = { name: collection.name, blob: collection.blob };

      if (mode === "private") {
        await Promise.all([
          updateCollectionShares(collectionType, collection.id, []),
          collection.visibility !== "private" &&
            updateCollection(collectionType, collection.id, {
              ...basePayload,
              visibility: "private",
            }),
        ]);
      } else if (mode === "org") {
        await Promise.all([
          updateCollectionShares(collectionType, collection.id, []),
          updateCollection(collectionType, collection.id, {
            ...basePayload,
            visibility: "public",
          }),
        ]);
      } else {
        // Specific people: set shares and ensure private visibility
        await Promise.all([
          updateCollectionShares(
            collectionType,
            collection.id,
            selectedUsers.map((u) => u.email)
          ),
          collection.visibility !== "private" &&
            updateCollection(collectionType, collection.id, {
              ...basePayload,
              visibility: "private",
            }),
        ]);
      }

      showSuccess("Sharing settings saved.");
      refreshCollections();
      onClose();
    } catch {
      showError("Failed to save sharing settings.");
    } finally {
      setSaving(false);
    }
  };

  const getUserLabel = (user: TenantUser) => {
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name ? `${name} (${user.email})` : user.email;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="share-dialog-title"
    >
      <DialogTitle id="share-dialog-title">
        Share "{collection.name}"
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControl>
            <FormLabel>Who can access this collection?</FormLabel>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value as SharingMode)}
              data-testid="share-dialog-mode-radio-group"
            >
              <FormControlLabel
                value="private"
                control={<Radio data-testid="share-mode-private-radio" />}
                label={
                  <Stack>
                    <Typography variant="body2">Private</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Only you can access this collection
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value="specific"
                control={<Radio data-testid="share-mode-specific-radio" />}
                label={
                  <Stack>
                    <Typography variant="body2">Specific people</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Share with selected teammates (read-only)
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value="org"
                control={<Radio data-testid="share-mode-org-radio" />}
                label={
                  <Stack>
                    <Typography variant="body2">Entire organization</Typography>
                    <Typography variant="caption" color="text.secondary">
                      All teammates can view this collection (read-only)
                    </Typography>
                  </Stack>
                }
              />
            </RadioGroup>
          </FormControl>

          {mode === "specific" && (
            <Autocomplete
              multiple
              options={allUsers}
              value={selectedUsers}
              onChange={(_, newValue) => setSelectedUsers(newValue)}
              getOptionLabel={getUserLabel}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              loading={loadingUsers}
              data-testid="share-dialog-user-picker"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select teammates"
                  placeholder="Search by name or email"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers && <CircularProgress size={18} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          data-testid="share-dialog-cancel-btn"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          data-testid="share-dialog-save-btn"
          onClick={handleSave}
          variant="contained"
          disabled={
            saving || (mode === "specific" && selectedUsers.length === 0)
          }
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
